import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import { T } from "../constants/theme";
import { CartContext } from "../contexts/CartContext";
import { AuthContext } from "../contexts/AuthContext";
import { ToastContext } from "../contexts/ToastContext";
import { WishlistContext } from "../contexts/WishlistContext";
import { requireLogin } from "../utils/authHelpers";

export default function KitItemCard({ item, onView }) {
  const navigate = useNavigate();
  const { addItem } = useContext(CartContext);
  const toast = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const { isWishlisted, toggleWishlist } = useContext(WishlistContext);

  const [hovered, setHovered] = useState(false);

  const isPlaceholder = !!item.placeholder;
  const wishlisted = !isPlaceholder && isWishlisted(item.id);
  const inStock = !isPlaceholder && Number(item.stock) > 0;
  const hasDiscount =
    !isPlaceholder && item.sale_price && Number(item.sale_price) < Number(item.price);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: isPlaceholder ? "default" : "pointer",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* IMAGE */}
      <div
        onClick={() => !isPlaceholder && onView(item.slug)}
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#F8F8F8",
          aspectRatio: "3 / 4",
        }}
      >
        {!isPlaceholder && item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform .7s ease",
              transform: hovered ? "scale(1.08)" : "scale(1)",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 56,
              color: "#C4C2BB",
            }}
          >
            🌱
          </div>
        )}

        {/* Dark Overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,.08)",
            opacity: hovered && !isPlaceholder ? 1 : 0,
            transition: ".3s",
          }}
        />

        {/* BADGES */}
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {isPlaceholder && (
            <div
              style={{
                background: "#fff",
                padding: "5px 10px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                color: T.muted,
              }}
            >
              Not Available Yet
            </div>
          )}
          {!isPlaceholder && !inStock && (
            <div
              style={{
                background: "#fff",
                padding: "5px 10px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".15em",
                textTransform: "uppercase",
              }}
            >
              Out Of Stock
            </div>
          )}
        </div>

        {/* HOVER BUTTONS */}
        {!isPlaceholder && (
          <div
            style={{
              position: "absolute",
              bottom: 20,
              left: 20,
              right: 20,
              display: "flex",
              gap: 10,
              opacity: hovered ? 1 : 0,
              transform: hovered ? "translateY(0)" : "translateY(25px)",
              transition: ".4s",
            }}
          >
            <button
              disabled={!inStock}
              onClick={(e) => {
                e.stopPropagation();
                if (!inStock) return;
                if (!requireLogin(user, navigate, toast, "Please sign in to add items to your shopping bag.")) {
                  return;
                }
                addItem(item.id);
                toast?.(`Added ${item.name} to cart`, "success");
              }}
              style={{
                flex: 1,
                height: 46,
                border: "none",
                background: "#fff",
                cursor: inStock ? "pointer" : "not-allowed",
                fontWeight: 700,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                fontSize: 11,
                color: inStock ? "#111" : "#aaa",
                transition: "background .35s ease, color .35s ease, box-shadow .35s ease, transform .35s ease",
                boxShadow: "none",
              }}
              onMouseEnter={(e) => {
                if (!inStock) return;
                e.currentTarget.style.background = T.gold;
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 24px rgba(196,154,60,.35)";
              }}
              onMouseLeave={(e) => {
                if (!inStock) return;
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#111";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {inStock ? "Add To Cart" : "Out Of Stock"}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(item.slug);
              }}
              style={{
                width: 46,
                border: "none",
                background: "#fff",
                cursor: "pointer",
                fontSize: 18,
              }}
            >
              ↗
            </button>
          </div>
        )}
      </div>

      {/* Wishlist */}
      {!isPlaceholder && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!requireLogin(user, navigate, toast, "Please sign in to save items to your wishlist.")) {
            return;
          }
          toggleWishlist({
            id: item.id,
            name: item.name,
            slug: item.slug,
            price: item.price,
            sale_price: item.sale_price,
            stock: item.stock,
            image: item.image_url,
          });
        }}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 42,
          height: 42,
          borderRadius: "50%",
          border: "none",
          outline: "none",
          background: wishlisted ? "#111" : "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 999,
          boxShadow: T.shadowSoft,
          transition: "all .3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.08)";
          if (!wishlisted) {
            e.currentTarget.style.background = "#F4F4F4";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          if (!wishlisted) {
            e.currentTarget.style.background = "#FFFFFF";
          }
        }}
      >
        {wishlisted ? (
          <FaHeart size={17} color="#FFFFFF" />
        ) : (
          <FiHeart size={17} color="#111111" />
        )}
      </button>
      )}

      {/* PRODUCT INFO */}
      <div
        style={{
          padding: "18px 10px 8px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: ".2em",
            marginBottom: 8,
          }}
        >
          {item.category || item.role}
        </div>

        <h3
          onClick={() => !isPlaceholder && onView(item.slug)}
          style={{
            margin: 0,
            cursor: isPlaceholder ? "default" : "pointer",
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.5,
            marginBottom: 12,
            minHeight: 24,
          }}
        >
          {item.name}
        </h3>

        {isPlaceholder ? (
          <div
            style={{
              fontSize: 13,
              color: T.muted,
              lineHeight: 1.5,
            }}
          >
            {item.description || "This kit item has not been added yet."}
          </div>
        ) : hasDiscount ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span style={{ color: "#111", fontWeight: 700, fontSize: 17 }}>
              £{parseFloat(item.sale_price).toFixed(2)}
            </span>
            <span
              style={{
                color: "#999",
                textDecoration: "line-through",
                fontSize: 14,
              }}
            >
              £{parseFloat(item.price).toFixed(2)}
            </span>
          </div>
        ) : (
          <div style={{ fontWeight: 700, fontSize: 17 }}>
            £{parseFloat(item.price).toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
}
